import { TuiElasticContainer } from '@taiga-ui/layout';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { TranslocoDirective } from '@jsverse/transloco';
import { LoaderComponent } from '@modules/shared/loader/loader.component';
import { Store } from '@ngrx/store';
import { PublicUser } from '@pixacare/domain';
import { Telemonitoring } from '@pixacare/dtos';
import { UserInputSelection } from '@shared/models/helpers/user-input-selection';
import { telemonitoringsActions } from '@shared/store/telemonitorings/telemonitorings.actions';
import { selectTelemonitoringUsers } from '@shared/store/telemonitorings/telemonitorings.selectors';
import { TuiButton, TuiDialogContext } from '@taiga-ui/core';
import { injectContext } from '@taiga-ui/polymorpheus';
import { first, map, Observable } from 'rxjs';
import { TelemonitoringCollaboratorsComponent } from '../telemonitoring-collaborators/telemonitoring-collaborators.component';

@Component({
  selector: 'pxc-telemonitoring-collaborators-dialog',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TelemonitoringCollaboratorsComponent,
    TuiElasticContainer,
    TuiButton,
    LoaderComponent,
    TranslocoDirective,
  ],
  templateUrl: './telemonitoring-collaborators-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TelemonitoringCollaboratorsDialogComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly tuiDialogContext = injectContext<TuiDialogContext<number[], { telemonitoring: Telemonitoring }>>();

  telemonitoringUsers$: Observable<UserInputSelection[]>;
  form$: Observable<UntypedFormGroup>;
  readonlyUserIds$: Observable<number[]>;

  telemonitoring = this.tuiDialogContext.data.telemonitoring;
  departmentIds = this.telemonitoring.departmentIds;

  collaborators: PublicUser[] = [];

  ngOnInit(): void {
    this.store.dispatch(
      telemonitoringsActions.getTelemonitoringUsers({
        telemonitoringId: this.telemonitoring.id,
      }),
    );

    this.telemonitoringUsers$ = this.store.select(selectTelemonitoringUsers(this.telemonitoring.id)).pipe(
      map((users) =>
        users.map((user) => ({
          user: {
            id: user.id,
            firstName: user.firstName,
            lastName: user.lastName,
            liveId: user.liveId,
            medicalSpecialtyLabel: '',
            clientNames: [],
          },
        })),
      ),
    );

    this.form$ = this.telemonitoringUsers$.pipe(
      first((users) => users.length > 0),
      map(
        (users) =>
          new FormGroup({
            collaborators: new FormControl(users),
          }),
      ),
    );

    this.readonlyUserIds$ = this.telemonitoringUsers$.pipe(map((users) => users.map((user) => user.user.id)));
  }

  submit(form: UntypedFormGroup): void {
    const collaboratorIds = form.get('collaborators').value.map((selection) => selection.user.id);
    this.tuiDialogContext.completeWith(collaboratorIds);
  }

  cancel(): void {
    this.tuiDialogContext.completeWith(null);
  }
}
